import { useState, useEffect, useRef } from 'react';
import { MapPin, Search, X, Loader2, Layers, GripVertical } from 'lucide-react';
import { cn } from '../../utils/cn';
import { isGoogleMapsConfigured } from '../../utils/googleMapsLoader';
import GoogleMapPicker from './GoogleMapPicker';

/**
 * MapPicker - Smart map picker with Google Maps / Leaflet+CartoDB fallback
 *
 * When Google Maps API key is configured:
 * - Uses native Google Maps for display
 * - Uses Google Geocoding for reverse geocoding
 *
 * When no API key (fallback):
 * - Uses Leaflet with CartoDB Voyager tiles (clean, free)
 * - Uses Nominatim for reverse geocoding
 */
export default function MapPicker({
  value, // { lat, lng, address }
  onChange,
  className,
  placeholder = 'Search for an address...',
  defaultCenter = { lat: -33.8688, lng: 151.2093 }, // Sydney, Australia default
  defaultZoom = 13,
  height = '300px',
  disabled = false,
  showSearch = true, // Allow hiding search when using LocationAutocomplete
  draggableMarker = true, // Enable draggable marker
  showSatelliteToggle = true // Show map/satellite toggle
}) {
  // Check if Google Maps is available
  const useGoogleMaps = isGoogleMapsConfigured();

  // If Google Maps is configured, use GoogleMapPicker
  if (useGoogleMaps) {
    return (
      <GoogleMapPicker
        value={value}
        onChange={onChange}
        className={className}
        defaultCenter={defaultCenter}
        defaultZoom={defaultZoom}
        height={height}
        disabled={disabled}
        draggableMarker={draggableMarker}
        showSatelliteToggle={showSatelliteToggle}
      />
    );
  }

  // Fallback to Leaflet + CartoDB
  return (
    <LeafletMapPicker
      value={value}
      onChange={onChange}
      className={className}
      placeholder={placeholder}
      defaultCenter={defaultCenter}
      defaultZoom={defaultZoom}
      height={height}
      disabled={disabled}
      showSearch={showSearch}
      draggableMarker={draggableMarker}
      showSatelliteToggle={showSatelliteToggle}
    />
  );
}

/**
 * LeafletMapPicker - Fallback map using Leaflet + CartoDB Voyager tiles
 */
function LeafletMapPicker({
  value,
  onChange,
  className,
  placeholder = 'Search for an address...',
  defaultCenter = { lat: -33.8688, lng: 151.2093 },
  defaultZoom = 13,
  height = '300px',
  disabled = false,
  showSearch = true,
  draggableMarker = true,
  showSatelliteToggle = true
}) {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [leafletModules, setLeafletModules] = useState(null);
  const [mapStyle, setMapStyle] = useState('street'); // 'street' or 'satellite'
  const [isDragging, setIsDragging] = useState(false);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  // Tile layer URLs - Using CartoDB Voyager for cleaner look (free, no API key needed)
  const tileUrls = {
    street: 'https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png',
    satellite: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'
  };

  const tileAttributions = {
    street: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
    satellite: '&copy; <a href="https://www.esri.com/">Esri</a> &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community'
  };

  // Dynamically load Leaflet to avoid SSR issues
  useEffect(() => {
    const loadLeaflet = async () => {
      try {
        const L = await import('leaflet');
        const { MapContainer, TileLayer, Marker, useMapEvents, useMap } = await import('react-leaflet');

        // Fix default marker icon issue
        delete L.default.Icon.Default.prototype._getIconUrl;
        L.default.Icon.Default.mergeOptions({
          iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
          iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
          shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
        });

        setLeafletModules({ L: L.default, MapContainer, TileLayer, Marker, useMapEvents, useMap });
        setMapLoaded(true);
      } catch (error) {
        console.error('Failed to load Leaflet:', error);
      }
    };

    loadLeaflet();
  }, []);

  // Search for address using Nominatim (free OpenStreetMap geocoding)
  const searchAddress = async (query) => {
    if (!query || query.length < 3) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    try {
      // Add Australia bias to search
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&countrycodes=au&limit=5`,
        {
          headers: {
            'Accept-Language': 'en-AU',
          }
        }
      );
      const data = await response.json();

      setSearchResults(data?.map(item => ({
        lat: parseFloat(item?.lat),
        lng: parseFloat(item?.lon),
        address: item?.display_name
      })) || []);
    } catch (error) {
      console.error('Address search failed:', error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  // Debounced search
  const handleSearchChange = (e) => {
    const query = e?.target?.value;
    setSearchQuery(query);
    setShowResults(true);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      searchAddress(query);
    }, 500);
  };

  // Select a search result
  const handleSelectResult = (result) => {
    onChange?.({
      lat: result?.lat,
      lng: result?.lng,
      address: result?.address
    });
    setSearchQuery(result?.address);
    setShowResults(false);
    setSearchResults([]);
  };

  // Clear selection
  const handleClear = () => {
    onChange?.(null);
    setSearchQuery('');
    setSearchResults([]);
  };

  // Reverse geocode coordinates to get address
  const reverseGeocode = async (lat, lng) => {
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`,
        {
          headers: {
            'Accept-Language': 'en-AU',
          }
        }
      );
      const data = await response.json();
      return data?.display_name || `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
    } catch (error) {
      console.error('Reverse geocode failed:', error);
      return `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
    }
  };

  // Map click handler component
  const MapClickHandler = () => {
    const { useMapEvents } = leafletModules;

    useMapEvents({
      click: async (e) => {
        if (disabled) return;

        const { lat, lng } = e?.latlng || {};
        const address = await reverseGeocode(lat, lng);

        onChange?.({ lat, lng, address });
        setSearchQuery(address);
      }
    });

    return null;
  };

  // Component to re-center map when value changes
  const MapCenterUpdater = () => {
    const { useMap } = leafletModules;
    const map = useMap();

    useEffect(() => {
      if (value?.lat && value?.lng && !isDragging) {
        map.setView([value.lat, value.lng], map.getZoom(), { animate: true });
      }
    }, [value?.lat, value?.lng, map]);

    return null;
  };

  // Handle marker drag end
  const handleMarkerDragEnd = async (e) => {
    const marker = e.target;
    const position = marker.getLatLng();
    const { lat, lng } = position;

    setIsDragging(false);

    // Reverse geocode to get new address
    const address = await reverseGeocode(lat, lng);
    onChange?.({ lat, lng, address });
    setSearchQuery(address);
  };

  // Get map center
  const mapCenter = value?.lat && value?.lng
    ? [value.lat, value.lng]
    : [defaultCenter.lat, defaultCenter.lng];

  return (
    <div className={cn('space-y-2', className)}>
      {/* Search Input - Only show if showSearch is true */}
      {showSearch && (
        <div className="relative">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-secondary" />
            <input
              type="text"
              value={searchQuery}
              onChange={handleSearchChange}
              onFocus={() => setShowResults(true)}
              placeholder={placeholder}
              disabled={disabled}
              className={cn(
                'w-full pl-10 pr-10 py-2 bg-surface border border-border rounded-lg',
                'text-text-primary placeholder-text-tertiary',
                'focus:outline-none focus:border-primary-orange',
                disabled && 'opacity-50 cursor-not-allowed'
              )}
            />
            {(searchQuery || value) && !disabled && (
              <button
                type="button"
                onClick={handleClear}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-text-secondary hover:text-text-primary"
              >
                <X className="w-4 h-4" />
              </button>
            )}
            {isSearching && (
              <Loader2 className="absolute right-10 top-1/2 -translate-y-1/2 w-4 h-4 text-text-secondary animate-spin" />
            )}
          </div>

          {/* Search Results Dropdown */}
          {showResults && searchResults.length > 0 && (
            <div className="absolute z-50 w-full mt-1 bg-surface border border-border rounded-lg shadow-lg max-h-48 overflow-y-auto">
              {searchResults?.map((result, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={() => handleSelectResult(result)}
                  className="w-full px-4 py-2 text-left text-sm text-text-primary hover:bg-surface-hover transition-colors first:rounded-t-lg last:rounded-b-lg"
                >
                  <div className="flex items-start gap-2">
                    <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0 text-primary-orange" />
                    <span className="line-clamp-2">{result?.address}</span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Map Container */}
      <div
        className={cn(
          'relative rounded-lg overflow-hidden border border-border',
          disabled && 'opacity-50 pointer-events-none'
        )}
        style={{ height }}
      >
        {mapLoaded && leafletModules ? (
          <>
            <leafletModules.MapContainer
              center={mapCenter}
              zoom={defaultZoom}
              style={{ height: '100%', width: '100%' }}
              ref={mapRef}
            >
              <leafletModules.TileLayer
                key={mapStyle}
                attribution={tileAttributions[mapStyle]}
                url={tileUrls[mapStyle]}
              />
              {value?.lat && value?.lng && (
                <leafletModules.Marker
                  position={[value.lat, value.lng]}
                  ref={markerRef}
                  draggable={draggableMarker && !disabled}
                  eventHandlers={{
                    dragstart: () => setIsDragging(true),
                    dragend: handleMarkerDragEnd
                  }}
                />
              )}
              <MapClickHandler />
              <MapCenterUpdater />
            </leafletModules.MapContainer>

            {/* Map Style Toggle */}
            {showSatelliteToggle && (
              <div className="absolute top-2 right-2 z-[1000]">
                <button
                  type="button"
                  onClick={() => setMapStyle(mapStyle === 'street' ? 'satellite' : 'street')}
                  className={cn(
                    'flex items-center gap-1.5 px-2 py-1.5 rounded-md text-xs font-medium',
                    'bg-white shadow-md border border-gray-200',
                    'hover:bg-gray-50 transition-colors'
                  )}
                  title={mapStyle === 'street' ? 'Switch to satellite view' : 'Switch to street view'}
                >
                  <Layers className="w-3.5 h-3.5" />
                  {mapStyle === 'street' ? 'Satellite' : 'Map'}
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="flex items-center justify-center h-full bg-background">
            <div className="text-center">
              <Loader2 className="w-8 h-8 text-primary-orange animate-spin mx-auto mb-2" />
              <p className="text-text-secondary text-sm">Loading map...</p>
            </div>
          </div>
        )}
      </div>

      {/* Coordinates Display and Drag Hint */}
      {value?.lat && value?.lng && (
        <div className="flex items-center justify-between">
          <p className="text-xs text-text-secondary">
            Coordinates: {value.lat.toFixed(6)}, {value.lng.toFixed(6)}
          </p>
          {draggableMarker && !disabled && (
            <p className="text-xs text-text-tertiary flex items-center gap-1">
              <GripVertical className="w-3 h-3" />
              Drag marker to adjust
            </p>
          )}
        </div>
      )}
    </div>
  );
}
